import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { defaultTo, noop } from 'lodash-es';

import { CmsEntities } from 'client/data/models/cms';
import { YOUTUBE_PREVIEW_SIZE } from 'client/utils/image-helpers';
import { ENTRY_ID, ENTRY_RENDER_TYPE, MOBILE_PLATFORM } from 'site-modules/editorial/constants/entry-render';
import { CAR_TYPES, PAGE_CAR_TYPES } from 'site-modules/shared/constants/upper-funnel/car-types';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { AdEntry } from 'site-modules/editorial/components/ad-entry/ad-entry';
import { ArticlePhoto } from 'site-modules/shared/components/editorial/article-photo/article-photo';
import { ArticleOffersAd } from 'site-modules/shared/components/native-ad/article-module-ad/article-offers-ad';
import { ArticleTable } from 'site-modules/shared/components/editorial/article-table/article-table';
import { EvRangeTable } from 'site-modules/editorial/pages/type/ev-range-table-graphql/ev-range-table';
import { ArticleAnchorSection } from 'site-modules/editorial/components/type/one-column/article-anchor-section/article-anchor-section';
import { ArticleVideos } from 'site-modules/editorial/components/article-videos/article-videos';
import { ColumnNewsWidget } from 'site-modules/shared/components/column-news-widget/column-news-widget';
import {
  EvNewsWidget,
  MakeNewsWidget,
  NewsWidget,
  TypeNewsWidget,
} from 'site-modules/shared/components/news-widget/news-widget';
import { ArticleContent } from 'site-modules/editorial/components/article-content/article-content';
import { VehicleTestingTeam } from 'site-modules/editorial/components/vehicle-testing-team/vehicle-testing-team';
import { VideoTypeModule } from 'site-modules/shared/components/video-module/video-module';
import { CompareModule } from 'site-modules/editorial/components/best/compare-module/compare-module';
import { InfographicEntry } from 'site-modules/editorial/components/infographic-entry/infographic-entry';

export function ResearchEntry({ entry, entriesProps, isMobile = false, useMxMdAuto = false }) {
  const { canonical, contentId, pageTitle, slug } = entriesProps;
  const entryPath = defaultTo(entriesProps.entryPath, []);
  const type = Object.values(PAGE_CAR_TYPES).includes(slug) ? slug : null;

  const platform = entry.metadata('platform').value();
  if (platform && (platform === MOBILE_PLATFORM) !== isMobile) {
    return null;
  }

  const entryRenderType = entry.metadata('renderType').value();
  const wide = entry.metadata('wide').boolean();
  const videoModuleProps = {
    videoLargeColumnSize: 8,
    playlistLargeColumnSize: 4,
    playlistHasCurrentVideo: true,
    isPlaylistSameHeight: true,
    hideVideoDetails: false,
    youtubeThumbnailSize: YOUTUBE_PREVIEW_SIZE.MAX_RES,
  };

  let renderEntry;

  if (entryRenderType) {
    switch (entryRenderType) {
      case ENTRY_RENDER_TYPE.ANCHOR_SECTION: {
        renderEntry = (
          <section
            className={classnames('one-column-content mx-1_5', {
              'mx-md-auto': useMxMdAuto,
              'mx-lg-auto': !useMxMdAuto,
            })}
          >
            <ArticleAnchorSection entry={entry} entriesProps={entriesProps} isMobile={isMobile} />
          </section>
        );
        break;
      }
      case ENTRY_RENDER_TYPE.AD: {
        renderEntry = <AdEntry entry={entry} isMobile={isMobile} toggleForceNoSeparator={noop} className="mb-1_5" />;
        break;
      }
      case ENTRY_RENDER_TYPE.HTML: {
        const content = entry.metadata('htmlContent').value();
        if (content) {
          renderEntry = (
            <ArticleContent entry={entry} content={content} highlight={entry.metadata('highlight').boolean()} />
          );
        }
        break;
      }
      case ENTRY_RENDER_TYPE.PHOTO: {
        const articlePhoto = (
          <ArticlePhoto
            photos={entry.links()}
            className={classnames('my-2_5', { wide })}
            showLineUnderTitle={false}
            aspectRatio="16:9"
          />
        );
        renderEntry = (
          <Fragment>
            <div className="hidden-lg-up" style={{ marginLeft: '-16px', marginRight: '-16px' }}>
              {articlePhoto}
            </div>
            <div className="hidden-lg-down" style={{ marginLeft: '-96px', marginRight: '-96px' }}>
              {articlePhoto}
            </div>
          </Fragment>
        );
        break;
      }
      case ENTRY_RENDER_TYPE.TABLE: {
        renderEntry =
          type && type.includes(CAR_TYPES.ELECTRIC) ? (
            <EvRangeTable
              type={CAR_TYPES.ELECTRIC}
              entry={entry}
              shareOptions={{
                contentId,
                canonical,
                entryPath: [...entryPath, entry.id],
                pageTitle,
              }}
              className="mt-2_5 mb-1_5"
            />
          ) : (
            <ArticleTable
              entry={entry}
              shareOptions={{
                contentId,
                canonical,
                entryPath: [...entryPath, entry.id],
                pageTitle,
              }}
              className="mt-2_5 mb-1_5"
            />
          );
        break;
      }
      case ENTRY_RENDER_TYPE.VIDEOS:
        renderEntry = (
          <div className="bg-gray-extra-light py-2 my-2_5">
            {type ? (
              <VideoTypeModule {...videoModuleProps} type={type} className="container border-0" isMobile={isMobile} />
            ) : (
              <ArticleVideos
                entry={entry}
                className="container border-0"
                videoModuleProps={videoModuleProps}
                isMobile={isMobile}
              />
            )}
          </div>
        );
        break;
      case ENTRY_RENDER_TYPE.ARTICLE_OFFERS_AD: {
        renderEntry = (
          <div className="bg-gray-extra-light py-2 px-0_5 my-2_5">
            <ArticleOffersAd entry={entry} className="one-column-data mx-auto" isMobile={isMobile} />
          </div>
        );
        break;
      }
      case ENTRY_RENDER_TYPE.CAR_NEWS_MODULE: {
        renderEntry = <ColumnNewsWidget className="one-column-content mx-0_5 mx-md-auto my-2_5" />;
        break;
      }
      case ENTRY_RENDER_TYPE.EV_NEWS_MODULE: {
        renderEntry = <EvNewsWidget className="container my-2_5" />;
        break;
      }
      case ENTRY_RENDER_TYPE.TYPE_NEWS_MODULE: {
        renderEntry = <TypeNewsWidget type={type} className="container my-2_5" />;
        break;
      }
      case ENTRY_RENDER_TYPE.NEWS_MODULE: {
        const makeName = entry.metadata('carNewsMake').value('');

        renderEntry = makeName ? (
          <MakeNewsWidget
            makeSlug={makeNiceName(makeName)}
            makeName={makeName}
            className="container my-2_5"
            withFallback={false}
          />
        ) : (
          <NewsWidget className="container my-2_5" withFallback={false} />
        );
        break;
      }
      case ENTRY_RENDER_TYPE.VEHICLE_TESTING_TEAM: {
        renderEntry = <VehicleTestingTeam />;
        break;
      }
      case ENTRY_RENDER_TYPE.INFO_GRAPHIC: {
        renderEntry = <InfographicEntry entry={entry} className="container my-2_5" />;
        break;
      }
      default: {
        break;
      }
    }
  }

  switch (entry.id) {
    case ENTRY_ID.COMPARE: {
      renderEntry = <CompareModule entry={entry} className="mb-1" />;
      break;
    }
    default: {
      break;
    }
  }

  if (!renderEntry) {
    return null;
  }

  return renderEntry;
}

ResearchEntry.propTypes = {
  entry: CmsEntities.Content.isRequired,
  entriesProps: PropTypes.shape({
    title: PropTypes.string,
    canonical: PropTypes.string,
    pathname: PropTypes.string,
    contentId: PropTypes.string,
    entryPath: PropTypes.arrayOf(PropTypes.string),
    slug: PropTypes.string,
  }).isRequired,
  isMobile: PropTypes.bool,
  useMxMdAuto: PropTypes.bool,
};
